export const modalFormData = {
    //The preview a document's mobile panel opens. One element, and it was
    //written out identically in twelve places before it had a name — five cards
    //and seven display views, byte for byte the same. A shared form is what
    //`modalFormRef` is for.
    /*
     * The document discount, opened from the menu under the totals. It submits
     * to the store rather than to the entity: that path re-runs the document's
     * own logic, which is the recalculation a document discount needs. Posting
     * it would save a half-calculated document.
     */
    documentDiscount: {
        id: 'documentDiscount',
        elements: [
            {
                type: 'switchSelect',
                id: 'documentDiscountType',
                displayField: 'documentDiscountTypeName',
                label: 'all.form.total.discount.type',
                entity: 'discountType',
                valueMap: {
                    'documentDiscountType': 'id',
                    'documentDiscountTypeName': 'name'
                },
                /*
                 * Nothing is preselected. This form writes straight into the
                 * document store, so a switch that auto-selects on mount would
                 * stamp a type the moment the modal opened — and opening it and
                 * changing your mind would leave the document claiming a 0%
                 * discount, rows and all. The user picks, or nothing happens.
                 */
                options: { autoSelectFirst: false }
            },
            {
                type: 'number',
                id: 'documentDiscountValue',
                label: 'all.form.total.discount.value',
                placeholder: 'all.form.total.discount.value.placeholder',
                decimals: 2,
                variant: 'price'
            }
        ],
        submit: { to: 'store' }
    },
    documentPreview: {
        id: 'documentPreview',
        elements: [
            { type: 'pdf' }
        ],
    },
    // An expense's documents on a phone: the generated one (from a purchase
    // order) and every file uploaded to it — the side panel's viewer.
    expenseDocuments: {
        id: 'expenseDocuments',
        elements: [
            {
                type: 'documentNavigator',
                entity: 'expense',
                generated: { condition: { field: 'source', operator: 'equals', value: 'purchaseOrder' } },
                folder: 'expenses',
                accept: '.pdf,.png,.jpg,.jpeg,.gif,.webp',
                // Expense documents are receipts and invoices: OCR them on upload,
                // then read them into a suggested expense (task 38.03).
                ocr: true,
                extract: 'expense'
            }
        ],
    },
    inventoryShipmentDesign: {
        id: 'inventoryShipmentDesign',
        elements: [
            {
                type: 'dataList',
                id: 'font',
                displayField: 'font',
                label: 'inventory.shipment.form.font',
                placeholder: 'inventory.shipment.form.font.placeholder',
                modalTitle: 'inventory.shipment.form.font.modal.title',
                entity: 'fonts',
                valueMap: {
                    'font': 'id',
                },
                required: true,
                defaultType: 'value',
                defaultValue: 'Helvetica',
                modalOpacity: 0
            },
            {
                type: 'dataList',
                id: 'layout',
                displayField: 'layoutName',
                label: 'inventory.shipment.form.layout',
                placeholder: 'inventory.shipment.form.layout.placeholder',
                modalTitle: 'inventory.shipment.form.layout.modal.title',
                entity: 'layouts',
                valueMap: {
                    'layout': 'id',
                    'layoutName': 'name',
                },
                required: true,
                defaultType: 'value',
                defaultValue: 'default',
                modalOpacity: 0
            }
        ],
    },
    inventoryShipmentReservations: {
        id: 'inventoryShipmentReservations',
        elements: [
            {
                //Two lists of the same document, so one widget twice. This was a
                //`tabList`: a component that read the strip out of `tabViewData`,
                //mapped every entry to a hardcoded `ReservationList`, and split
                //the chrome from the content across two model files. A tab is a
                //multiSelect with its own source and predicate (33.2 step 10b).
                type: 'multiSelect',
                id: 'shipment.reservation',
                load: false,
                tabs: [
                    {
                        id: 'unreservedTab',
                        key: 'unreservedStock',
                        label: 'inventory.reservation.tabs.toReserve',
                        icon: 'unlock',
                        //The document's own line allocations, flattened, each
                        //carrying its line's product name.
                        source: {
                            from: 'lines[].locations[]',
                            copy: [{ itemName: 'lines[].itemName[i]' }]
                        },
                        //The ones no reservation of this document claims.
                        partitions: [
                            {
                                id: 'available',
                                condition: {
                                    none: '$reservations',
                                    field: 'reservationId', operator: 'equals', valueFrom: 'parent.reservationId'
                                }
                            }
                        ],
                    columns: [
                        {
                            field: 'itemName',
                            title: 'inventory.list.title.product',
                            flex: '3',
                            margin: '0 0.75rem 0 0'
                        },
                        {
                            field: 'locationId',
                            title: 'inventory.list.title.location',
                            flex: '4',
                            margin: '0'
                        },
                        {
                            field: 'quantity',
                            title: 'inventory.list.title.quantity',
                            flex: '1',
                            margin: '0'
                        }
                    ],
                        footer: {
                            description: 'inventory.shipment.form.reservation.unReserved.description'
                        },
                        emptyMessage: 'inventory.shipment.form.reservation.unReserved.no.data',
                        action: {
                            buttonLabel: 'inventory.shipment.form.reservation.unReserved.buttonLabel',
                            endpoint: 'stockShipment/:id/reserve',
                            body: {
                                id: '${record.id}',
                                entity: '${record.entity}',
                                lines: '${record.lines}'
                            },
                            name: 'reserve',
                            invalidateQuery: 'stockShipment'
                        }
                    },
                    {
                        id: 'reservedTab',
                        key: 'reservedStock',
                        label: 'inventory.reservation.tabs.reserved',
                        icon: 'lock',
                        //A different list, not a narrower one — which is what the
                        //retired `value: 'isNotUndefined'` enum actually meant.
                        source: { from: 'reservations' },
                        partitions: [
                            { id: 'available' }
                        ],
                    columns: [
                        {
                            field: 'itemName',
                            title: 'inventory.list.title.product',
                            flex: '3',
                            margin: '0 0.75rem 0 0'
                        },
                        {
                            field: 'locationId',
                            title: 'inventory.list.title.location',
                            flex: '4',
                            margin: '0'
                        },
                        {
                            field: 'quantity',
                            title: 'inventory.list.title.quantity',
                            flex: '1',
                            margin: '0'
                        }
                    ],
                        footer: {
                            description: 'inventory.shipment.form.reservation.reserved.description'
                        },
                        emptyMessage: 'inventory.shipment.form.reservation.reserved.no.data',
                        action: {
                            buttonLabel: 'inventory.shipment.form.reservation.reserved.buttonLabel',
                            endpoint: 'stockShipment/:id/unreserve',
                            body: {
                                id: '${record.id}',
                                entity: '${record.entity}',
                                reservations: '${record.reservations}'
                            },
                            name: 'unreserve',
                            invalidateQuery: 'stockShipment'
                        }
                    }
                ],
                list: 'multiList',
                //Rows with an action beneath them, not a choice.
                selectable: false
            }
        ],
    },
    invoiceDesign: {
        id: 'invoiceDesign',
        elements: [
            {
                type: 'dataList',
                id: 'font',
                displayField: 'font',
                label: 'invoice.form.font',
                placeholder: 'invoice.form.font.placeholder',
                modalTitle: 'invoice.form.font.modal.title',
                entity: 'fonts',
                valueMap: {
                    'font': 'id',
                },
                required: true,
                defaultType: 'value',
                defaultValue: 'Helvetica',
                modalOpacity: 0
            },
            {
                type: 'dataList',
                id: 'layout',
                displayField: 'layoutName',
                label: 'invoice.form.layout',
                placeholder: 'invoice.form.layout.placeholder',
                modalTitle: 'invoice.form.layout.modal.title',
                entity: 'layouts',
                valueMap: {
                    'layout': 'id',
                    'layoutName': 'name',
                },
                required: true,
                defaultType: 'value',
                defaultValue: 'default',
                modalOpacity: 0
            }
        ],
    },
    invoiceOptions: {
        id: 'invoiceOptions',
        elements: [
            {
                type: "currency",
                id: "currency",
                displayField: "currency",
                parameter: "date",
                label: "invoice.form.currency",
                placeholder: "invoice.form.currency.placeholder",
                modalTitle: "invoice.form.currency.modal.title",
                entity: "currency",
                valueMap: {
                    currency: "id",
                    currencyName: "name",
                    exchangeRate: "exchangeRate",
                },
                options: { withHeaders: true, displayId: true },
                required: true,
                defaultType: "currency",
                linkedFields: ['lines', , 'campaignId', 'priceListId']
            },
            {
                type: "select",
                id: "language",
                displayField: "languageName",
                label: "invoice.form.language",
                placeholder: "invoice.form.language.placeholder",
                modalTitle: "invoice.form.language.modal.title",
                entity: "language",
                valueMap: {
                    language: "id",
                    languageName: "name",
                },
                options: { withHeaders: false },
                required: true,
                defaultType: "select"
            },
            {
                type: 'connect',
                id: 'campaignId',
                displayField: 'campaignCode',
                label: 'invoice.form.campaign',
                placeholder: 'invoice.form.campaign.placeholder',
                modalTitle: "invoice.form.campaign.modal.title",
                entity: 'campaign',
                valueMap: {
                    'campaignId': 'id',
                    'campaignCode': 'code',
                    'campaignName': 'name'
                },
                options: { withHeaders: true },
                required: true,
                defaultType: 'select',
                linkedFields: ['lines', 'currency', 'priceListId']
            },
            {
                type: 'connect',
                id: 'priceListId',
                displayField: 'priceListId',
                label: 'invoice.form.priceList',
                placeholder: 'invoice.form.priceList.placeholder',
                modalTitle: "invoice.form.priceList.modal.title",
                entity: 'priceList',
                valueMap: {
                    'priceListId': 'id',
                    'priceListName': 'idName'
                },
                options: { withHeaders: true },
                required: true,
                defaultType: 'select',
                linkedFields: ['lines', 'currency']
            },
        ],
    },
    invoicePayments: {
        id: 'invoicePayments',
        elements: [
            {
                type: 'switch',
                id: 'customPayments',
                switchText: 'Enable Custom Payments',
            },
            {
                type: 'cardSection',
                id: 'paymentDate',
                title: 'invoice.form.payments.modal.title',
                body: {
                    description: 'invoice.form.payments.modal.body',
                    mergeParams: ['numPayments'],
                },
                footer: {
                    description: 'invoice.form.payments.modal.footer',
                    mergeParams: ['$totalGrossSalesAmount', 'currency'],
                    sumField: 'paymentAmount',
                    warning: {
                        tagVariant: 'danger',
                        tagLabel: 'invoice.form.payments.modal.warning.label',
                        field: 'totalGrossSalesAmount',
                        message: 'invoice.form.payments.modal.warning.message',
                        mergeParams: ['currency']
                    }
                },
                section: {
                    id: 'invoicePaymentsLines',
                    meta: {
                        type: 'lines',
                        linesArray: 'payments',
                        linesAddAction: 'invoice.form.payment.add.item',
                        width: '800px',
                    },
                    groups: [
                        {
                            type: 'group',
                            id: 'paymentDateAmountDue',
                            focus: true,
                            fields: [
                                {
                                    type: 'date',
                                    id: 'paymentDate',
                                    label: 'invoice.form.paymentDate',
                                    placeholder: 'invoice.form.paymentDate.placeholder',
                                    defaultType: 'date'
                                },
                                {
                                    type: 'number',
                                    id: 'paymentAmount',
                                    label: 'invoice.form.paymentAmount',
                                    placeholder: 'invoice.form.netPrice',
                                    required: true,
                                    decimals: 2,
                                    defaultType: 'value',
                                    defaultValue: 1,
                                    variant: 'amount'
                                },
                            ]
                        },
                        {
                            type: 'timestamp',
                            id: 'timeStamp'
                        },
                        {
                            type: 'hidden',
                            id: 'paymentCurrency',
                            statePath: 'currency'
                        }
                    ],
                    buttons: [
                        {
                            type: 'button',
                            id: 'invoicePayments.paymentDate.delete',
                            action: 'delete',
                            label: 'all.form.button.delete',
                            icon: 'delete',
                            variant: 'danger',
                            size: 'icon'
                        },
                        {
                            type: 'button',
                            id: 'invoicePayments.paymentDate.save',
                            action: 'save',
                            label: 'all.form.button.save',
                            variant: 'primary'
                        }
                    ],
                    displaySection: {
                        type: 'lines',
                        emptyField: 'paymentAmount',
                        emptyMessage: 'invoice.form.payment.missing.message',
                        editButtonRightOffset: '25%',
                        linesArray: 'payments',
                        columns: [
                            {
                                weight: 7,
                                basis: '70%',
                                elements: [
                                    {
                                        type: 'niceDate',
                                        value: 'paymentDate',
                                        scale: 'title'
                                    }
                                ]
                            },
                            {
                                weight: 3,
                                basis: '30%',
                                align: 'end',
                                elements: [
                                    {
                                        type: 'amount',
                                        value: 'paymentAmount',
                                        scale: 'title'
                                    },
                                    {
                                        type: 'currency',
                                        value: 'currency',
                                        scale: 'normal'
                                    }
                                ],
                            }
                        ]
                    }
                }
            }
        ],
    },
    /*
     * Match an expense with money going out — matchPayment's purchase-side
     * mirror. The expense itself is the one row on the left (an expense has no
     * payment tranches), read by id; the suggestions are outgoing bank
     * transactions, compared with it as magnitudes because a payment out is
     * stored negative.
     */
    matchExpensePayment: {
        id: 'matchExpensePayment',
        elements: [
            {
                type: 'stateForm',
                targets: ['expense', 'paymentTransaction'],
                suggestions: {
                    type: 'pickList',
                    listType: 'selectList',
                    margins: '0 0 1rem 0',
                    layout: {
                        leftTitle: { field: 'description' },
                        leftContent: { field: 'date' },
                        rightTitle: { field: 'amountLeft', decorator: 'currency' },
                        rightContent: { field: 'confidenceScore', style: 'tag' }
                    },
                    dataFormat: { 'date': 'date', 'amountLeft': 'amount', 'confidenceScore': 'percentage' },
                    emptyMessage: 'purchases.expense.matchPayment.empty.message',
                    dataInherit: { 'paymentCurrency': 'currency' },
                    listId: 'id',
                    testId: 'expensePaymentSuggestion',
                    dataSource: 'parent',
                    source: 'suggestions',
                    action: {
                        buttonLabel: 'purchases.expense.matchPayment.action',
                        endpoint: 'expense/${selection.id}/match',
                        body: {
                            origin: 'expense',
                            destination: 'paymentTransaction',
                            originId: '${selection.id}',
                            destinationId: '${item.paymentAccountId}',
                            destinationSubId: '${item.id}'
                        }
                    }
                },
                suggestionOptions: {
                    dateFields: ['paymentDate', 'date'],
                    amountFields: ['amountDue', 'amount'],
                    amountSigns: [1, -1],
                    // An expense booked before it carried an amountDue
                    amountFallbackFields: ['totalGrossAmount', null]
                },
                modalForm: {
                    id: 'matchExpensePaymentSuggestions',
                    elements: [
                        {
                            type: 'pickList',
                            source: 'expense',
                            dataSource: 'parent',
                            dataFormat: { 'paymentDate': 'date', 'amountDue': 'amount' },
                            dataInherit: { 'paymentCurrency': 'currency' },
                            listId: 'id',
                            margins: '0 0 1rem 0',
                            testId: 'matchExpensePaymentSuggestions',
                            columns: [
                                { field: 'paymentDate', title: 'purchases.expense.matchPayment.table.date', check: true, flex: "2", margin: '0' },
                                { field: 'amountDue', title: 'purchases.expense.matchPayment.table.amount', decorator: 'paymentCurrency', tag: false, flex: "1", margin: '0' }
                            ],
                            preselectFirst: true,
                            emptyMessage: 'purchases.expense.matchPayment.empty.message'
                        },
                        {
                            type: 'description',
                            text: 'purchases.expense.matchPayment.description'
                        }
                    ],
                },
                load: [
                    {
                        endpoint: 'expense/$id',
                        single: true
                    },
                    {
                        endpoint: 'paymentAccount/all/paymentTransaction',
                        filter: { isPositive: false, fullyMatched: false }
                    }]
            },

        ],
    },
    matchPayment: {
        id: 'matchPayment',
        elements: [
            {
                type: 'stateForm',
                targets: ['invoicePayment', 'paymentTransaction'],
                suggestions: {
                    type: 'pickList',
                    listType: 'selectList',
                    margins: '0 0 1rem 0',
                    layout: {
                        leftTitle: { field: 'description' },
                        leftContent: { field: 'date' },
                        rightTitle: { field: 'amountLeft', decorator: 'currency' },
                        rightContent: { field: 'confidenceScore', style: 'tag' }
                    },
                    dataFormat: { 'date': 'date', 'amountLeft': 'amount', 'confidenceScore': 'percentage' },
                    emptyMessage: 'invoice.matchPayment.empty.message',
                    dataInherit: { 'paymentCurrency': 'currency' },
                    listId: 'id',
                    testId: 'paymentSuggestion',
                    dataSource: 'parent',
                    source: 'suggestions',
                    action: {
                        buttonLabel: 'invoice.form.match.payment.action',
                        //`selection` is the row chosen in the list above — the one
                        //`preselectFirst` picks — and `item` is the suggestion
                        //pressed. `origin` and `destination` were `targets[0]` and
                        //`targets[1]`, read from this element's own config, so they
                        //are literals rather than context (33.2 step 9).
                        endpoint: 'invoicePayment/${selection.id}/match',
                        body: {
                            origin: 'invoicePayment',
                            destination: 'paymentTransaction',
                            originId: '${selection.id}',
                            destinationId: '${item.paymentAccountId}',
                            destinationSubId: '${item.id}'
                        }
                    }
                },
                // Compared on what is still due, not the instalment's full
                // amount — a part-paid one, or several summed, owe less.
                // An instalment booked before amountDue existed owes it all.
                suggestionOptions: {
                    amountFields: ['amountDue', 'amount'],
                    amountFallbackFields: ['paymentAmount', null]
                },
                modalForm: {
                    id: 'matchPaymentSuggestions',
                    elements: [
                        {
                            type: 'description',
                            text: 'invoice.matchPayment.expected',
                            linkedFields: ['numPayments'],
                            mergeParams: ['numPayments']
                        },
                        {
                            type: 'pickList',
                            source: 'invoicePayment',
                            dataSource: 'parent',
                            dataFormat: { 'paymentDate': 'date', 'paymentAmount': 'amount' },
                            dataInherit: { 'paymentCurrency': 'currency' },
                            listId: 'paymentId',
                            margins: '0 0 1rem 0',
                            testId: 'matchPaymentSuggestions',
                            columns: [
                                { field: 'paymentDate', title: 'invoice.matchPayment.table.date', check: true, flex: "2", margin: '0' },
                                { field: 'amountDue', title: 'invoice.matchPayment.table.amount', decorator: 'paymentCurrency', tag: false, flex: "1", margin: '0' }
                            ],
                            // Several instalments can be paid by one transfer:
                            // the suggestions are then searched for their total
                            singleSelect: false,
                            preselectFirst: true,
                            emptyMessage: 'invoice.matchPayment.empty.message'
                        },
                        {
                            type: 'description',
                            text: 'invoice.matchPayment.description'
                        }
                    ],
                },
                load: [
                    {
                        endpoint: 'invoicePayment',
                        filter: { invoiceId: '$id', paid: false }
                    },
                    {
                        endpoint: 'paymentAccount/all/paymentTransaction',
                        filter: { isPositive: true, fullyMatched: false }
                    }]
            },

        ],
    },
    purchaseOrderDesign: {
        id: 'purchaseOrderDesign',
        elements: [
            {
                type: 'dataList',
                id: 'font',
                displayField: 'font',
                label: 'purchases.purchaseOrder.form.font',
                placeholder: 'purchases.purchaseOrder.form.font.placeholder',
                modalTitle: 'purchases.purchaseOrder.form.font.modal.title',
                entity: 'fonts',
                valueMap: {
                    'font': 'id',
                },
                required: true,
                defaultType: 'value',
                defaultValue: 'Helvetica',
                modalOpacity: 0
            },
            {
                type: 'dataList',
                id: 'layout',
                displayField: 'layoutName',
                label: 'purchases.purchaseOrder.form.layout',
                placeholder: 'purchases.purchaseOrder.form.layout.placeholder',
                modalTitle: 'purchases.purchaseOrder.form.layout.modal.title',
                entity: 'layouts',
                valueMap: {
                    'layout': 'id',
                    'layoutName': 'name',
                },
                required: true,
                defaultType: 'value',
                defaultValue: 'default',
                modalOpacity: 0
            }
        ],
    },
    purchaseOrderOptions: {
        id: 'purchaseOrderOptions',
        elements: [
            {
                type: "currency",
                id: "currency",
                displayField: "currency",
                parameter: "date",
                label: "purchases.purchaseOrder.form.currency",
                placeholder: "purchases.purchaseOrder.form.currency.placeholder",
                modalTitle: "purchases.purchaseOrder.form.currency.modal.title",
                entity: "currency",
                valueMap: {
                    currency: "id",
                    currencyName: "name",
                    exchangeRate: "exchangeRate",
                },
                options: { withHeaders: true, displayId: true },
                required: true,
                defaultType: "currency",
                linkedFields: ['lines']
            },
            {
                type: "select",
                id: "language",
                displayField: "languageName",
                label: "purchases.purchaseOrder.form.language",
                placeholder: "purchases.purchaseOrder.form.language.placeholder",
                modalTitle: "purchases.purchaseOrder.form.language.modal.title",
                entity: "language",
                valueMap: {
                    language: "id",
                    languageName: "name",
                },
                options: { withHeaders: false },
                required: true,
                defaultType: "select"
            },
            {
                type: 'select',
                id: 'paymentTermId',
                displayField: 'paymentTermName',
                label: 'purchases.purchaseOrder.form.paymentTerm',
                modalTitle: 'purchases.purchaseOrder.form.paymentTerm.modal.title',
                entity: 'paymentTerm',
                valueMap: {
                    'paymentTermId': 'id',
                    'paymentTermName': 'name',
                    'paymentTermDays': 'days',
                },
                options: { withHeaders: false, displayId: true },
                defaultType: 'select',
                linkedFields: ['paymentTermId', 'date', 'paymentTermDays', 'paymentDate']
            },
        ],
    },
    quoteDesign: {
        id: 'quoteDesign',
        elements: [
            {
                type: 'dataList',
                id: 'font',
                displayField: 'font',
                label: 'quote.form.font',
                placeholder: 'quote.form.font.placeholder',
                modalTitle: 'quote.form.font.modal.title',
                entity: 'fonts',
                valueMap: {
                    'font': 'id',
                },
                required: true,
                defaultType: 'value',
                defaultValue: 'Helvetica',
                modalOpacity: 0
            },
            {
                type: 'dataList',
                id: 'layout',
                displayField: 'layoutName',
                label: 'quote.form.layout',
                placeholder: 'quote.form.layout.placeholder',
                modalTitle: 'quote.form.layout.modal.title',
                entity: 'layouts',
                valueMap: {
                    'layout': 'id',
                    'layoutName': 'name',
                },
                required: true,
                defaultType: 'value',
                defaultValue: 'default',
                modalOpacity: 0
            }
        ],
    },
    quoteOptions: {
        id: 'quoteOptions',
        elements: [
            {
                type: "currency",
                id: "currency",
                displayField: "currency",
                parameter: "date",
                label: "quote.form.currency",
                placeholder: "quote.form.currency.placeholder",
                modalTitle: "quote.form.currency.modal.title",
                entity: "currency",
                valueMap: {
                    currency: "id",
                    currencyName: "name",
                    exchangeRate: "exchangeRate",
                },
                options: { withHeaders: true, displayId: true },
                required: true,
                defaultType: "currency",
                linkedFields: ['lines', , 'campaignId', 'priceListId']
            },
            {
                type: "select",
                id: "language",
                displayField: "languageName",
                label: "quote.form.language",
                placeholder: "quote.form.language.placeholder",
                modalTitle: "quote.form.language.modal.title",
                entity: "language",
                valueMap: {
                    language: "id",
                    languageName: "name",
                },
                options: { withHeaders: false },
                required: true,
                defaultType: "select"
            },
            {
                type: 'connect',
                id: 'campaignId',
                displayField: 'campaignCode',
                label: 'quote.form.campaign',
                placeholder: 'quote.form.campaign.placeholder',
                modalTitle: "quote.form.campaign.modal.title",
                entity: 'campaign',
                valueMap: {
                    'campaignId': 'id',
                    'campaignCode': 'code',
                    'campaignName': 'name'
                },
                options: { withHeaders: true },
                required: true,
                defaultType: 'select',
                linkedFields: ['lines', 'currency', 'priceListId']
            },
            {
                type: 'connect',
                id: 'priceListId',
                displayField: 'priceListId',
                label: 'quote.form.priceList',
                placeholder: 'quote.form.priceList.placeholder',
                modalTitle: "quote.form.priceList.modal.title",
                entity: 'priceList',
                valueMap: {
                    'priceListId': 'id',
                    'priceListName': 'idName'
                },
                options: { withHeaders: true },
                required: true,
                defaultType: 'select',
                linkedFields: ['lines', 'currency']
            },
        ],
    },
    routingVersionBomInputs: {
        id: 'routingVersionBomInputs',
        elements: [
            {
                type: 'multiSelect',
                valueMap: {
                    bomLineId: 'productId',
                    productId: 'productId',
                    productName: 'productName',
                    productSku: 'productSku',
                    qty: 'qty',
                    uom: 'uom',
                    uomName: 'uomName'
                },
                source: {
                    endpoint: 'product/${record.productId}/bomVersion/${record.bomVersionId}',
                    path: 'lines',
                    idField: 'productId',
                    //Without a BOM version there is nothing to ask for. This was
                    //an early return in the widget; it is a predicate now, so
                    //lint:conditions checks it like every other one.
                    condition: { field: 'bomVersionId', operator: 'exists' }
                },
                testId: 'routingVersion.operations.section.line',
                columns: [
                    {
                        field: 'productName',
                        title: 'routingVersion.form.operation.inputs.col.product',
                        check: true,
                        flex: '3',
                        margin: '0'
                    },
                    {
                        field: 'qty',
                        title: 'routingVersion.form.operation.inputs.col.qty',
                        decorator: 'uomName',
                        flex: '1',
                        margin: '0'
                    }
                ],
                linesArray: 'inputs',
                list: 'multiList',
                //Every component of the BOM is on offer and none is spoken for,
                //so there is one group and it takes everything.
                partitions: [
                    { id: 'available' }
                ],
                //A BOM line has no id of its own, so `source.idField` stamps one
                //from the product and this is the valueMap key that stores it.
                identity: 'bomLineId',
                //No `allocate`: ticking a component copies its quantity across
                //rather than drawing down a demand, which valueMap already says.
                countField: 'inputsCount',
                emptyMessage: 'routingVersion.form.operation.inputs.empty',
                notReadyMessage: 'routingVersion.form.operation.inputs.no.bom'
            }
        ],
    },
    selectFromLocation: {
        id: 'selectFromLocation',
        elements: [
            {
                type: 'multiSelect',
                valueMap: {
                    locationId: 'id',
                    warehouseId: 'warehouseId',
                    warehouseCode: 'warehouseCode',
                    hallId: 'hallId',
                    hallCode: 'hallCode',
                    rackId: 'rackId',
                    rackCode: 'rackCode',
                    level: 'level',
                    position: 'position'
                },
                source: {
                    endpoint: 'product/${item.itemId}/itemLocation'
                },
                listItems: 'location',
                testId: 'movement.lines.section.line',
                load: true,
                columns: [
                    {
                        field: 'locationId',
                        title: 'inventory.list.title.location',
                        check: true,
                        flex: '2',
                        margin: '0'
                    },
                    {
                        field: 'availableQuantity',
                        title: 'inventory.list.title.quantity',
                        decorator: 'unitName',
                        tag: false,
                        flex: '1',
                        margin: '0'
                    }
                ],
                subObject: 'origin',
                list: 'multiList',
                partitions: [
                    {
                        //Rows this document already reserved against this line.
                        //`filterItems` decided this in code and read the line by
                        //splitting a `<line>.<n>` string; `lineIndex` is a field
                        //now and the comparison is `equals` (33.2 steps 7 and 8).
                        id: 'reserved',
                        condition: {
                            all: [
                                { field: 'reservedQuantity', operator: 'gt', value: 0 },
                                {
                                    some: 'reservations',
                                    all: [
                                        { field: 'documentId', operator: 'equals', valueFrom: 'context.docId' },
                                        { field: 'lineIndex', operator: 'equals', valueFrom: 'context.lineIndex' }
                                    ]
                                }
                            ]
                        }
                    },
                    {
                        //Everything else with stock left to take. A location that
                        //is neither is in no list at all, which is what the
                        //if/else this replaces did.
                        id: 'available',
                        condition: { field: 'availableQuantity', operator: 'gt', value: 0 }
                    }
                ],
                allocate: 'constraint',
                singleSelect: true,
                constraint: 'itemQuantity',
                sumField: 'availableQuantity',
                emptyMessage: 'inventory.shipment.form.lines.select.locations.no.data'
            }
        ],
    },
    selectFromLocationWarehouse: {
        id: 'selectFromLocationWarehouse',
        elements: [
            {
                type: 'multiSelect',
                valueMap: {
                    fromLocationId: 'id',
                    warehouseId: 'warehouseId',
                    warehouseCode: 'warehouseCode',
                    hallId: 'hallId',
                    hallCode: 'hallCode',
                    rackId: 'rackId',
                    rackCode: 'rackCode',
                    level: 'level',
                    position: 'position'
                },
                source: {
                    endpoint: 'product/${item.itemId}/itemLocation'
                },
                listItems: 'location',
                testId: 'warehouseOrder.lines.section.line',
                load: true,
                columns: [
                    {
                        field: 'locationId',
                        title: 'inventory.list.title.location',
                        check: true,
                        flex: '2',
                        margin: '0'
                    },
                    {
                        field: 'availableQuantity',
                        title: 'inventory.list.title.quantity',
                        decorator: 'unitName',
                        tag: false,
                        flex: '1',
                        margin: '0'
                    }
                ],
                list: 'multiList',
                partitions: [
                    {
                        //Rows this document already reserved against this line.
                        //`filterItems` decided this in code and read the line by
                        //splitting a `<line>.<n>` string; `lineIndex` is a field
                        //now and the comparison is `equals` (33.2 steps 7 and 8).
                        id: 'reserved',
                        condition: {
                            all: [
                                { field: 'reservedQuantity', operator: 'gt', value: 0 },
                                {
                                    some: 'reservations',
                                    all: [
                                        { field: 'documentId', operator: 'equals', valueFrom: 'context.docId' },
                                        { field: 'lineIndex', operator: 'equals', valueFrom: 'context.lineIndex' }
                                    ]
                                }
                            ]
                        }
                    },
                    {
                        //Everything else with stock left to take. A location that
                        //is neither is in no list at all, which is what the
                        //if/else this replaces did.
                        id: 'available',
                        condition: { field: 'availableQuantity', operator: 'gt', value: 0 }
                    }
                ],
                allocate: 'constraint',
                singleSelect: true,
                constraint: 'itemQuantity',
                sumField: 'availableQuantity',
                emptyMessage: 'inventory.shipment.form.lines.select.locations.no.data'
            }
        ],
    },
    selectLocations: {
        id: 'selectLocations',
        elements: [
            {
                type: 'multiSelect',
                valueMap: {
                    locationId: 'id',
                    warehouseId: 'warehouseId',
                    warehouseCode: 'warehouseCode',
                    hallId: 'hallId',
                    hallCode: 'hallCode',
                    rackId: 'rackId',
                    rackCode: 'rackCode',
                    level: 'level',
                    position: 'position',
                    unitId: 'unitId',
                    unitName: 'unitName'
                },
                source: {
                    endpoint: 'product/${item.itemId}/itemLocation'
                },
                listItems: 'location',
                testId: 'shipment.lines.section.line',
                load: true,
                header: {
                    description: 'inventory.shipment.form.lines.select.locations.body'
                },
                footer: {
                    description: 'inventory.shipment.form.lines.select.locations.footer',
                    mergeParams: ['allocatedQuantity', 'itemQuantity'],
                    warning: {
                        tagVariant: 'yellow',
                        tagLabel: 'inventory.shipment.form.lines.locations.modal.warning.label',
                        field: 'itemQuantity',
                        message: 'inventory.shipment.form.lines.locations.modal.warning.message'
                    }
                },
                columns: [
                    {
                        field: 'locationId',
                        title: 'inventory.list.title.location',
                        check: true,
                        flex: '2',
                        margin: '0'
                    },
                    {
                        field: 'availableQuantity',
                        title: 'inventory.list.title.quantity',
                        decorator: 'unitName',
                        tag: true,
                        flex: '1',
                        margin: '0'
                    }
                ],
                linesArray: 'locations',
                list: 'multiList',
                partitions: [
                    {
                        //Rows this document already reserved against this line.
                        //`filterItems` decided this in code and read the line by
                        //splitting a `<line>.<n>` string; `lineIndex` is a field
                        //now and the comparison is `equals` (33.2 steps 7 and 8).
                        id: 'reserved',
                        condition: {
                            all: [
                                { field: 'reservedQuantity', operator: 'gt', value: 0 },
                                {
                                    some: 'reservations',
                                    all: [
                                        { field: 'documentId', operator: 'equals', valueFrom: 'context.docId' },
                                        { field: 'lineIndex', operator: 'equals', valueFrom: 'context.lineIndex' }
                                    ]
                                }
                            ]
                        },
                        //How much of the row is this line's, which the reserved
                        //rows show as a tag. Only this config asks for it: the
                        //other two set `tag: false` on the quantity column, so
                        //they computed the total and threw it away.
                        sum: {
                            of: 'reservations',
                            condition: {
                                all: [
                                    { field: 'documentId', operator: 'equals', valueFrom: 'context.docId' },
                                    { field: 'lineIndex', operator: 'equals', valueFrom: 'context.lineIndex' }
                                ]
                            },
                            field: 'quantity',
                            as: 'displayQuantity'
                        }
                    },
                    {
                        //Everything else with stock left to take. A location that
                        //is neither is in no list at all, which is what the
                        //if/else this replaces did.
                        id: 'available',
                        condition: { field: 'availableQuantity', operator: 'gt', value: 0 }
                    }
                ],
                allocate: 'constraint',
                constraint: 'itemQuantity',
                sumField: 'availableQuantity',
                emptyMessage: 'inventory.shipment.form.lines.select.locations.no.data'
            }
        ],
    },
}
